<template>
  <div>
    <b-card :title="$t('booking.title')">
      <b-card-text class="d-flex justify-content-between align-items-center">
        <p class="mb-0">
          {{ $t("booking.subtitle") }}
        </p>
        <b-button
          variant="primary"
          @click="openBookingModal"
        >
          {{ $t("booking.create_booking") }}
        </b-button>
      </b-card-text>

      <!-- ── FILTERS ── -->
      <b-row class="mt-2 mb-1">
        <b-col md="3">
          <AutoComplete
            v-model="filters.patientCard"
            :label="$t('booking.patient_card')"
            :placeholder="$t('booking.patient_card')"
            :serializer="identitySerializer"
            endpoint="patientCard"
            text-title="patient.name"
            no-sort
            other-query="sort=-id&filter[is_active]=1"
            @input="onFilterPatientSelect"
          >
            <template #option="option">
              {{ resolvePatientCardId(option) }}
            </template>
            <template #selected-option="option">
              {{ resolvePatientCardId(option) }}
            </template>
          </AutoComplete>
        </b-col>

        <b-col md="3">
          <AutoComplete
            v-model="filters.doctor"
            :label="$t('booking.doctor')"
            :placeholder="$t('booking.doctor')"
            :serializer="identitySerializer"
            endpoint="doctor"
            text-title="name"
            no-sort
            other-query="sort=-id"
            @input="onFilterDoctorSelect"
          >
            <template #option="option">
              {{ option.full_name || option.name || option.id }}
            </template>
            <template #selected-option="option">
              {{ option.full_name || option.name || option.id }}
            </template>
          </AutoComplete>
        </b-col>

        <b-col md="2">
          <b-form-group
            :label="$t('booking.status')"
            label-for="filter-status"
          >
            <b-form-select
              id="filter-status"
              v-model="filters.status"
              :options="statusOptions"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group
            label=" من تاريخ"
            label-for="filter-from-date"
          >
            <b-form-input
              id="filter-from-date"
              v-model="filters.from_date"
              type="date"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group
            label=" الى تاريخ"
            label-for="filter-to-date"
          >
            <b-form-input
              id="filter-to-date"
              v-model="filters.to_date"
              type="date"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          class="d-flex justify-content-end"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="clearFilters"
          >
            اعادة تعيين
          </b-button>
        </b-col>
      </b-row>

      <!-- ── TABLE ── -->
      <b-table
        v-if="!tableLoading"
        :items="bookings"
        :fields="bookingColumns"
        primary-key="id"
        responsive
        show-empty
        :empty-text="$t('common.no_data')"
        class="mt-2"
      >
        <template #cell(patient)="row">
          {{ getPatientCardName(row.item.patient_card) }}
        </template>

        <template #cell(doctor)="row">
          {{ getDoctorName(row.item.doctor) }}
        </template>

        <template #cell(consultation_date)="row">
          {{ formatConsultationDateDisplay(row.item.consultation_date) }}
        </template>

        <template #cell(status)="row">
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-form-select
              v-model="row.item.status"
              :options="statusOptions.filter(o => o.value !== null)"
              size="sm"
              :disabled="updatingStatusId === row.item.id"
              style="width: 130px;"
              @change="updateStatus(row.item)"
            />
            <b-spinner
              v-if="updatingStatusId === row.item.id"
              small
              variant="primary"
            />
          </div>
        </template>

        <template #cell(actions)="row">
          <div
            class="d-flex"
            style="gap: 6px;"
          >
            <b-button
              size="sm"
              variant="outline-primary"
              @click="openEditModal(row.item)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
              />
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="confirmDelete(row.item)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>
          </div>
        </template>
      </b-table>

      <div
        v-else
        class="text-center py-2"
      >
        <b-spinner small />
      </div>

      <!-- ── PAGINATION ── -->
      <div class="d-flex justify-content-between align-items-center mt-2">
        <span class="text-muted small">
          {{ $t('common.total') }}: {{ pagination.total }}
        </span>
        <b-pagination
          v-model="pagination.currentPage"
          :total-rows="pagination.total"
          :per-page="pagination.perPage"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="onPageChange"
        />
        <b-form-select
          v-model="pagination.perPage"
          :options="[10, 15, 25, 50]"
          style="width: auto;"
          @change="onPerPageChange"
        />
      </div>
    </b-card>

    <!-- ── CREATE / EDIT MODAL ── -->
    <b-modal
      id="booking-create-modal"
      :title="isEditMode ? $t('booking.edit_modal_title') : $t('booking.modal_title')"
      :no-close-on-backdrop="true"
      size="lg"
      centered
      @hidden="resetForm"
    >
      <b-row>
        <b-col md="6">
          <AutoComplete
            v-model="bookingForm.patientCard"
            :label="$t('booking.patient_card')"
            :placeholder="$t('booking.patient_card')"
            :serializer="identitySerializer"
            endpoint="patientCard"
            text-title="patient.name"
            no-sort
            other-query="sort=-id&filter[is_active]=1"
            @input="onPatientCardSelect"
          >
            <template #option="option">
              <span>{{ resolvePatientCardId(option) }}</span>
            </template>
            <template #selected-option="option">
              <span>{{ resolvePatientCardId(option) }}</span>
            </template>
          </AutoComplete>
          <p class="text-danger mt-1">
            {{ errorForm.patient_card_id }}
          </p>
        </b-col>

        <b-col md="6">
          <AutoComplete
            v-model="bookingForm.doctor"
            :label="$t('booking.doctor')"
            :placeholder="$t('booking.doctor')"
            :serializer="identitySerializer"
            endpoint="doctor"
            text-title="name"
            no-sort
            other-query="sort=-id"
            @input="onDoctorSelect"
          >
            <template #option="option">
              <span>{{ option.full_name || option.name || option.id }}</span>
            </template>
            <template #selected-option="option">
              <span>{{ option.full_name || option.name || option.id }}</span>
            </template>
          </AutoComplete>
          <p class="text-danger mt-1">
            {{ errorForm.doctor_id }}
          </p>
        </b-col>

        <b-col md="6">
          <b-form-group
            :label="$t('booking.status')"
            label-for="form-status"
          >
            <b-form-select
              id="form-status"
              v-model="bookingForm.status"
              :options="statusOptions.filter(o => o.value !== null)"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            :label="$t('booking.consultation_date')"
            label-for="consultation_date"
          >
            <b-form-input
              id="consultation_date"
              v-model="bookingForm.consultation_date"
              type="datetime-local"
            />
          </b-form-group>
          <p class="text-danger mt-1">
            {{ errorForm.consultation_date }}
          </p>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="$t('booking.notes')"
            label-for="notes"
          >
            <b-form-textarea
              id="notes"
              v-model="bookingForm.notes"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <hr>
      <div class="d-flex align-items-center mb-2">
        <h5 class="mb-0">
          {{ $t("booking.services_title") }}
        </h5>
      </div>
      <p class="text-danger mt-1">
        {{ errorForm.services }}
      </p>
      <ServicePriceAutoComplete
        v-model="selectedServices"
        :label="$t('booking.service')"
        :placeholder="$t('booking.service')"
        :serializer="identitySerializer"
        param-query="search"
        text-title="title"
        :multiple="true"
        md="6"
        @val="handleServicesChange"
        @total-price="handleTotalPrice"
      />

      <template #modal-footer>
        <b-button
          variant="secondary"
          :disabled="loading"
          @click="$bvModal.hide('booking-create-modal')"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="loading"
          @click="submitBooking"
        >
          <span v-if="!loading">{{ $t("booking.submit") }}</span>
          <b-spinner
            v-else
            small
          />
        </b-button>
      </template>
    </b-modal>

    <!-- ── DELETE CONFIRM MODAL ── -->
    <b-modal
      id="booking-delete-modal"
      :title="$t('common.confirm_delete')"
      centered
    >
      <p>{{ $t('booking.delete_confirm_message') }}</p>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="secondary"
          @click="cancel()"
        >
          {{ $t('common.cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="loading"
          @click="deleteBooking"
        >
          <span v-if="!loading">{{ $t('common.delete') }}</span>
          <b-spinner
            v-else
            small
          />
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BButton,
  BTable,
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const ServicePriceAutoComplete = () => import('@/components/common/ServicePriceAutoComplete.vue')

export default {
  components: {
    BCard,
    BCardText,
    BButton,
    BTable,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BSpinner,
    BPagination,
    AutoComplete,
    ServicePriceAutoComplete,
  },

  data() {
    return {
      loading: false,
      tableLoading: true,
      updatingStatusId: null,
      isEditMode: false,
      editingId: null,
      deletingId: null,
      bookings: [],
      selectedServices: [],
      totalServicesPrice: 0,
      bookingForm: this.getInitialForm(),
      errorForm: {
        patient_card_id: '',
        doctor_id: '',
        consultation_date: '',
        services: '',
      },
      filters: {
        patientCard: null,
        patient_card_id: null,
        doctor: null,
        doctor_id: null,
        status: null,
        from_date: '',
        to_date: '',
      },
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
      },
    }
  },

  computed: {
    bookingColumns() {
      return [
        { key: 'patient', label: this.$t('booking.patient_card') },
        { key: 'doctor', label: this.$t('booking.doctor') },
        { key: 'consultation_date', label: this.$t('booking.consultation_date') },
        { key: 'status', label: this.$t('booking.status') },
        { key: 'actions', label: this.$t('common.actions') },
      ]
    },
    statusOptions() {
      return [
        { value: null, text: this.$t('common.all') },
        { value: 'pending', text: this.$t('booking.status_pending') },
        { value: 'completed', text: this.$t('booking.status_completed') },
        { value: 'cancelled', text: this.$t('booking.status_cancelled') },
      ]
    },
  },

  mounted() {
    this.loadInitData()
  },

  methods: {
    // ── Helpers ───────────────────────────────────────────────────────────────

    identitySerializer(val) {
      return val
    },
    resolvePatientCardId(option) {
      return option?.patient?.name || ''
    },
    getDoctorName(option) {
      return option?.full_name || option?.name || '-'
    },
    getPatientCardName(option) {
      return this.resolvePatientCardId(option)
    },
    formatConsultationDateDisplay(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDateTime(dateTimeValue) {
      return dateTimeValue ? `${dateTimeValue}:00` : ''
    },
    getCurrentDateTimeLocal() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
    },

    // ── Query builder ─────────────────────────────────────────────────────────

    buildQuery() {
      const { currentPage, perPage } = this.pagination
      const {
        patient_card_id, doctor_id, status, from_date, to_date,
      } = this.filters

      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        sort: '-id',
        include: 'patient_card,doctor,services',
      })

      if (patient_card_id) params.append('patient_card_id', patient_card_id)
      if (doctor_id) params.append('doctor_id', doctor_id)
      if (status) params.append('status', status)
      if (from_date) params.append('from_date', from_date)
      if (to_date) params.append('to_date', to_date)

      return params.toString()
    },

    // ── Data loading ──────────────────────────────────────────────────────────

    async loadInitData() {
      this.tableLoading = true
      try {
        await this.getBookings()
      } finally {
        this.tableLoading = false
      }
    },

    async getBookings() {
      this.tableLoading = true
      try {
        await this.getList(`/consultation-bookings?${this.buildQuery()}`)
        this.bookings = this.all_data?.data || []
        this.pagination.total = this.all_data?.meta?.total ?? this.bookings.length
      } finally {
        this.tableLoading = false
      }
    },

    // ── Filters ───────────────────────────────────────────────────────────────

    onFilterPatientSelect(item) {
      this.filters.patient_card_id = item?.id || null
      this.applyFilters()
    },
    onFilterDoctorSelect(item) {
      this.filters.doctor_id = item?.id || null
      this.applyFilters()
    },
    applyFilters() {
      this.pagination.currentPage = 1
      this.getBookings()
    },
    clearFilters() {
      this.filters = {
        patientCard: null,
        patient_card_id: null,
        doctor: null,
        doctor_id: null,
        status: null,
        from_date: '',
        to_date: '',
      }
      this.applyFilters()
    },

    // ── Pagination ────────────────────────────────────────────────────────────

    onPageChange(page) {
      this.pagination.currentPage = page
      this.getBookings()
    },
    onPerPageChange() {
      this.pagination.currentPage = 1
      this.getBookings()
    },

    // ── Inline status update ──────────────────────────────────────────────────

    async updateStatus(item) {
      this.updatingStatusId = item.id
      try {
        await this.$store.dispatch('put', {
          path: `/consultation-bookings/${item.id}`,
          form: { status: item.status },
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('booking.status_updated'),
          },
        })
      } catch {
        // Revert optimistic change by reloading from server
        await this.getBookings()
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('booking.error_generic'),
          },
        })
      } finally {
        this.updatingStatusId = null
      }
    },

    // ── Form helpers ──────────────────────────────────────────────────────────

    getInitialForm() {
      return {
        patient_card_id: '',
        patientCard: null,
        doctor_id: '',
        doctor: null,
        consultation_date: this.getCurrentDateTimeLocal?.() || '',
        status: 'pending',
        notes: '',
        services: [],
      }
    },
    resetErrors() {
      this.errorForm = {
        patient_card_id: '', doctor_id: '', consultation_date: '', services: '',
      }
    },
    resetForm() {
      this.isEditMode = false
      this.editingId = null
      this.bookingForm = this.getInitialForm()
      this.selectedServices = []
      this.totalServicesPrice = 0
      this.resetErrors()
      this.loading = false
    },
    openBookingModal() {
      this.resetForm()
      this.$bvModal.show('booking-create-modal')
    },

    // ── Edit ──────────────────────────────────────────────────────────────────

    openEditModal(item) {
      this.resetForm()
      this.isEditMode = true
      this.editingId = item.id

      this.bookingForm.patient_card_id = item.patient_card?.id || ''
      this.bookingForm.patientCard = item.patient_card || null
      this.bookingForm.doctor_id = item.doctor?.id || ''
      this.bookingForm.doctor = item.doctor || null
      this.bookingForm.status = item.status || 'pending'
      this.bookingForm.notes = item.notes || ''
      this.bookingForm.consultation_date = item.consultation_date
        ? item.consultation_date.slice(0, 16)
        : this.getCurrentDateTimeLocal()
      this.bookingForm.services = item.services || []
      this.selectedServices = item.services || []

      this.$bvModal.show('booking-create-modal')
    },

    // ── Delete ────────────────────────────────────────────────────────────────

    confirmDelete(item) {
      this.deletingId = item.id
      this.$bvModal.show('booking-delete-modal')
    },
    async deleteBooking() {
      if (!this.deletingId) return
      this.loading = true
      try {
        await this.$store.dispatch('delete', {
          path: `/consultation-bookings/${this.deletingId}`,
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('booking.success_deleted'),
          },
        })
        this.$bvModal.hide('booking-delete-modal')
        await this.getBookings()
      } catch {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('booking.error_generic'),
          },
        })
      } finally {
        this.loading = false
        this.deletingId = null
      }
    },

    // ── Form field events ─────────────────────────────────────────────────────

    onPatientCardSelect(item) {
      this.bookingForm.patient_card_id = item?.id || ''
      this.errorForm.patient_card_id = ''
    },
    onDoctorSelect(item) {
      this.bookingForm.doctor_id = item?.id || ''
      this.errorForm.doctor_id = ''
    },
    handleServicesChange(services) {
      this.selectedServices = services
      this.bookingForm.services = Array.isArray(services)
        ? services
        : services ? [services] : []
      this.errorForm.services = ''
    },
    handleTotalPrice(total) {
      this.totalServicesPrice = total
    },

    // ── Validation ────────────────────────────────────────────────────────────

    validateForm() {
      this.resetErrors()
      let isValid = true

      if (!this.bookingForm.patient_card_id) {
        this.errorForm.patient_card_id = this.$t('booking.validation_patient_card_required')
        isValid = false
      }
      if (!this.bookingForm.doctor_id) {
        this.errorForm.doctor_id = this.$t('booking.validation_doctor_required')
        isValid = false
      }
      if (!this.bookingForm.consultation_date) {
        this.errorForm.consultation_date = this.$t('booking.validation_consultation_date_required')
        isValid = false
      }
      if (!this.bookingForm.services.length) {
        this.errorForm.services = this.$t('booking.validation_services_required')
        isValid = false
      }
      this.bookingForm.services.forEach(service => {
        if (!service?.id) {
          isValid = false
          this.errorForm.services = this.$t('booking.validation_service_required')
        }
      })
      return isValid
    },

    // ── Submit ────────────────────────────────────────────────────────────────

    buildPayload() {
      return {
        patient_card_id: this.bookingForm.patient_card_id,
        doctor_id: this.bookingForm.doctor_id,
        status: this.bookingForm.status || 'pending',
        consultation_date: this.formatDateTime(this.bookingForm.consultation_date),
        notes: this.bookingForm.notes || '',
        services: this.bookingForm.services.map(service => ({
          service_id: service.id,
          quantity: 1,
        })),
      }
    },
    showErrorToasts(errors) {
      Object.values(errors).forEach(errorItem => {
        const message = Array.isArray(errorItem) ? errorItem[0] : errorItem
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('validation.error'),
            variant: 'danger',
            text: message,
          },
        })
      })
    },
    async submitBooking() {
      if (!this.validateForm()) return
      this.loading = true
      const payload = this.buildPayload()
      try {
        if (this.isEditMode) {
          await this.$store.dispatch('put', {
            path: `/consultation-bookings/${this.editingId}`,
            form: payload,
          })
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.success'),
              variant: 'success',
              text: this.$t('booking.success_updated'),
            },
          })
        } else {
          await this.$store.dispatch('create', {
            path: '/consultation-bookings',
            form: payload,
          })
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.success'),
              variant: 'success',
              text: this.$t('booking.success_created'),
            },
          })
        }
        this.$bvModal.hide('booking-create-modal')
        this.resetForm()
        await this.getBookings()
      } catch (error) {
        if (error?.response?.status === 400 && error?.response?.data) {
          this.showErrorToasts(error.response.data)
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('booking.error_generic'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
